import { z } from 'zod';

import { CodeLanguage } from './languages.js';

/** 32 random bytes as hex, generated in the browser on first visit (plan §4.5). */
export const OwnerToken = z.string().regex(/^[0-9a-f]{64}$/);
export const OWNER_TOKEN_HEADER = 'x-owner-token';

/** Largest workspace JSON accepted, in characters (plan §4.10). */
export const MAX_WORKSPACE_CHARS = 256 * 1024;

export const Workspace = z
  .record(z.string(), z.unknown())
  .refine((w) => JSON.stringify(w).length <= MAX_WORKSPACE_CHARS, 'workspace too large');
export type Workspace = z.infer<typeof Workspace>;

/** Largest typed program accepted, in characters. */
export const MAX_SOURCE_CHARS = 64 * 1024;

/** The language a student types in (ADR 0012). The code view still shows all four. */
export const SourceLanguage = z.enum(['cpp', 'py', 'js']);
export type SourceLanguage = z.infer<typeof SourceLanguage>;

export const SourceCode = z.string().max(MAX_SOURCE_CHARS);

export const LastRun = z.object({
  status: z.enum(['finished', 'error', 'step-limit', 'stopped']),
  /** First line or two of the output, for the home card and the live activity feed. */
  summary: z.string().max(200),
  at: z.string(),
});
export type LastRun = z.infer<typeof LastRun>;

export const ProjectDto = z.object({
  id: z.string(),
  exerciseSlug: z.string().nullable(),
  title: z.string(),
  workspace: Workspace,
  /** The text the student typed, when they work in code mode; blocks are rebuilt from it. */
  source: SourceCode.nullable(),
  sourceLang: SourceLanguage.nullable(),
  lang: CodeLanguage.nullable(),
  rev: z.number().int(),
  solved: z.boolean(),
  lastRun: LastRun.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectDto = z.infer<typeof ProjectDto>;

export const ProjectSummary = ProjectDto.omit({ workspace: true });
export type ProjectSummary = z.infer<typeof ProjectSummary>;

export const ProjectListResponse = z.object({ projects: z.array(ProjectSummary) });
export type ProjectListResponse = z.infer<typeof ProjectListResponse>;

export const CreateProjectRequest = z.object({
  exerciseSlug: z.string().nullable().optional(),
  title: z.string().trim().min(1).max(80).optional(),
  /** Start from this workspace instead of the exercise's starter (tests, "save a copy"). */
  workspace: Workspace.optional(),
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequest>;

export const UpdateProjectRequest = z.object({
  /** The revision the client last saw; a stale one gets 409 with the current project. */
  rev: z.number().int().nonnegative(),
  workspace: Workspace.optional(),
  /** Sent together with the workspace while the student types (ADR 0012). */
  source: SourceCode.nullable().optional(),
  sourceLang: SourceLanguage.nullable().optional(),
  lang: CodeLanguage.nullable().optional(),
  title: z.string().trim().min(1).max(80).optional(),
  lastRun: LastRun.optional(),
});
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequest>;

export const ConflictResponse = z.object({
  error: z.literal('stale'),
  project: ProjectDto,
});
export type ConflictResponse = z.infer<typeof ConflictResponse>;
